import type { CreateSectorDto } from "@/dtos/CreateSectorDto";
import type { UpdateSectorDto } from "@/dtos/UpdateSectorDto";
import api from "@/services/api";
import { useMutation, useQuery } from "@tanstack/react-query";

interface GetSectorsParams {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}

export const useGetSectors = (params?: GetSectorsParams) => {
  const page = params?.page || 1;
  const limit = params?.limit || 10;
  const status = params?.status;
  const search = params?.search;

  return useQuery({
    queryKey: ["getSectors", page, limit, status, search],
    queryFn: async () => {
      const response = await api.get("/sectors", {
        params: {
          page,
          limit,
          ...(status && status !== "all" && { status }),
          ...(search && { search }),
        },
      });

      return response.data;
    },
  });
};

export const useCreateSector = () => {
  return useMutation({
    mutationKey: ["createSector"],
    mutationFn: async (createSectorData: CreateSectorDto) => {
      const response = await api.post("/sectors", createSectorData);

      return response.data;
    },
  });
};

export const useGetSectorById = (id: string) => {
  return useQuery({
    queryKey: ["getSectorById", id],
    queryFn: async () => {
      const response = await api.get(`/sectors/${id}`);

      return response.data;
    },
  });
};

export const useUpdateSector = () => {
  return useMutation({
    mutationKey: ["updateSector"],
    mutationFn: async (updateSectorData: UpdateSectorDto) => {
      const response = await api.patch(`/sectors/${updateSectorData.id}`, {
        name: updateSectorData.name,
        acronym: updateSectorData.acronym,
        description: updateSectorData.description,
        responsibleId: updateSectorData.responsibleId,
        email: updateSectorData.email,
        phone: updateSectorData.phone,
        workingHoursStart: updateSectorData.workingHoursStart,
        workingHoursEnd: updateSectorData.workingHoursEnd,
        hasRegionals: updateSectorData.hasRegionals,
        status: updateSectorData.status,
      });

      return response.data;
    },
  });
};

export const useDeleteSector = () => {
  return useMutation({
    mutationKey: ["deleteSector"],
    mutationFn: async (id: string) => {
      const response = await api.delete(`/sectors/${id}`);

      return response.data;
    },
  });
};
